import React, { FC, useState, useMemo, CSSProperties, useRef, useEffect, ReactNode, } from "react";
// 组件
import { LeftOutlined, RightOutlined, } from '@ant-design/icons';
// 方法
import classNames from "classnames";
import MenuTreeContext, { IMenuTreeContext } from './menuTreeContext';
import { getElementTop } from "../utils/utils";
// 常量
import { MenuTreeProps, MenuTreeChildrenEnum, SubMenuProps, MenuItemProps, } from "./interface";
import './style.less';
/** 菜单 */
const MenuTree: FC<MenuTreeProps> = props => {
  const { 
    onSelect, onOpen, animation, isSelectSubMenu, defaultSelectedKey, defaultOpenSubMenus, defaultOpenSubMenusAll, selectedKey,
    openSubMenus, hideTitle, children, border, className, style, collapsed: outCollapsed, collapsible, bottomHeight, onCollapse,
  } = props;
  const [collapsed, setCollapsed] = useState<boolean>(outCollapsed || false);
  const [value, setValue] = useState<string | undefined>(defaultSelectedKey);
  const [openKeys, setOpenKeys] = useState<string[]>(openSubMenus || defaultOpenSubMenus || []);
  const [groupValue, setGroupValue] = useState<string>();
  const menuTreeRef = useRef<HTMLUListElement>(null);
  const isFirst = useRef<boolean>(true);
  const timer = useRef<any>();
  useMemo(() => {
    setCollapsed(outCollapsed || false);
  }, [outCollapsed]);
  useEffect(() => {
    if (menuTreeRef.current) {
      handleSetMaxHeight(menuTreeRef.current);
    }
    if (timer.current) {
      clearInterval(timer.current);
    }
    timer.current = setInterval(() => {
      if (menuTreeRef.current) {
        clearInterval(timer.current);
        handleSetMaxHeight(menuTreeRef.current);
      }
    }, 100);
  }, []);
  useEffect(() => {
    selectedKey !== undefined && setValue(selectedKey)
  }, [selectedKey]);
  useEffect(() => {
    openSubMenus !== undefined && setOpenKeys(openSubMenus);
  }, [openSubMenus]);
  /** 设置最大高度 */
  const handleSetMaxHeight = (menuTreeNode: HTMLUListElement) => {
    const elementTop = getElementTop(menuTreeNode);
    const maxHeight = document.body.clientHeight - elementTop - (collapsible ? 34 : 2) - (bottomHeight ?? 0);
    menuTreeNode.style.maxHeight = `${maxHeight}px`;
  };
  const handleSelect = (key: string, label: any, parentKey?: string) => {
    if (key === value) return;
    onSelect?.(key, label, parentKey);
    setGroupValue(parentKey);
    if (selectedKey) return;
    setValue(key);
  };
  const handleOpen = (isOpen: boolean, key: string, label: any) => {
    onOpen?.(isOpen, key, label);
    if (openSubMenus) return;
    const newOpens = isOpen ? [...openKeys, key] : openKeys.filter(v => v !== key);
    setOpenKeys(newOpens);
  };
  const passedContext: IMenuTreeContext = {
    animation,
    selectedKey: selectedKey || value,
    selectedSubMenuKey: groupValue,
    openSubMenus: openKeys || defaultOpenSubMenus,
    isSelectSubMenu: isSelectSubMenu,
    onSelect: handleSelect,
    onOpen: handleOpen,
  };
  const classes = useMemo(() => {
    return classNames("yb-menu-tree-wrap", className, {
      border: border,
      collapsed: collapsed,
      collapsible: collapsible,
    });
  }, [border, className, collapsed, collapsible]);
  const renderChildren = () => {
    const childrenNode: ReactNode[] = [];
    const allSubMenuKey: string[] = [];
    React.Children.forEach(children, (child, index) => {
      const childElement = child as React.FunctionComponentElement<SubMenuProps | MenuItemProps>;
      const { displayName } = childElement.type
      if (displayName && displayName in MenuTreeChildrenEnum) {
        if (displayName === MenuTreeChildrenEnum.SubMenu && childElement.key) {
          allSubMenuKey.push(childElement.props.value);
        }
        const newChildren: ReactNode = React.cloneElement(childElement, {
          collapsed,
          titleTtooltip: hideTitle ? false : undefined,
          index: childElement.props.value,
          key: childElement.props.value,
          ...childElement.props,
        });
        childrenNode.push(newChildren);
      } else {
        console.error("Warning: Menu has a child which is not a MenuItem component")
      }
    })
    if (isFirst.current && defaultOpenSubMenusAll && childrenNode.length) {
      setOpenKeys(allSubMenuKey);
      isFirst.current = false;
    }
    return childrenNode;
  }
  const ulStyle: CSSProperties = useMemo(() => {
    return {
      overflowY: 'auto',
      ...style,
    };
  }, [style]);

  return (
    <div className={classes} style={ulStyle}>
      <ul className='yb-menu-tree' ref={menuTreeRef}>
        <MenuTreeContext.Provider value={passedContext}>
          {renderChildren()}
        </MenuTreeContext.Provider>
      </ul>
        {
          collapsible && (
            <div className="yb-menu-tree-collapsible">
              <div
                className="yb-menu-tree-collapsible-icon"
                onClick={() => {
                  setCollapsed(prev => {
                    const nextValue = !prev;
                    onCollapse?.(nextValue);
                    return nextValue;
                  });
                }}
              >
                {collapsed ? <RightOutlined /> : <LeftOutlined />}
              </div>
            </div>
          )
        }
    </div>
  );
};

MenuTree.defaultProps = {
  isSelectSubMenu: false,
  defaultOpenSubMenusAll: false,
  border: false,
  animation: true,
  collapsed: false,
  collapsible: false,
};

export default MenuTree;
